import type { ReactNode } from 'react';
import { ArrowRightLeft, Check, Search } from 'lucide-react';
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
  Input,
  InputDecoration,
  InputGroup,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@monksflow/monks-ui';
import { MenuSeparator } from './MenuSeparator';
import { RAIL_POPOVER_SIDE_OFFSET } from './railPopover.constants';
import type { Workspace } from '@/entities/workspace';
import { useTranslation } from 'react-i18next';

export interface SidebarWorkspacePopoverProps {
  /** Whether the popover is open. */
  open: boolean;
  /** Called when the popover open state should change. */
  onOpenChange: (open: boolean) => void;
  /** Current search query for filtering workspaces. */
  searchQuery: string;
  /** Called when the search query changes. */
  onSearchChange: (query: string) => void;
  /** The filtered (and sorted) workspace list to display. */
  workspaces: Workspace[];
  /** ID of the currently selected workspace. */
  selectedWorkspaceId: string | null;
  /** Called when a workspace is selected. */
  onSelect: (workspace: Workspace) => void;
  /** Organization display name (shown in the header section). */
  organizationName?: string | null;
  /** URL for the organization logo. */
  organizationLogoUrl?: string | null;
  /** Organization initials (fallback when no logo). */
  organizationInitials?: string | null;
  /** Whether an organization header action is available. */
  hasHeaderAction?: boolean;
  /** Tooltip text for the header action button. */
  headerActionTooltip?: string;
  /** Called when the header action button is clicked. */
  onHeaderAction?: () => void;
  /** The trigger element (typically SidebarWorkspaceTrigger). */
  children: ReactNode;
}

/**
 * Workspace selector popover — drops down from the sidebar workspace trigger.
 *
 * Layout (Figma):
 * 1. Organization section (if org name provided): logo/initials + name + switch org button
 * 2. "Change workspace" label
 * 3. Search input
 * 4. Scrollable workspace list (sorted, filtered)
 * 5. Each workspace: avatar/logo + name, selected shows Check icon
 *
 * Width: ~248px, max-height ~332px
 */
export function SidebarWorkspacePopover({
  open,
  onOpenChange,
  searchQuery,
  onSearchChange,
  workspaces,
  selectedWorkspaceId,
  onSelect,
  organizationName,
  organizationLogoUrl,
  organizationInitials,
  hasHeaderAction = false,
  headerActionTooltip,
  onHeaderAction,
  children,
}: SidebarWorkspacePopoverProps) {
  const { t } = useTranslation('common');

  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>{children}</PopoverTrigger>
      <PopoverContent
        side="right"
        sideOffset={RAIL_POPOVER_SIDE_OFFSET}
        align="start"
        collisionPadding={8}
        onOpenAutoFocus={(event) => event.preventDefault()}
        className="flex w-[248px] max-h-[min(420px,calc(100dvh-32px))] flex-col overflow-hidden p-0"
      >
        {/* Organization section */}
        {organizationName && (
          <>
            <div className="flex items-center gap-2 px-3 py-2.5">
              <Avatar size="mini" roundness="rounded">
                {organizationLogoUrl ? (
                  <AvatarImage src={organizationLogoUrl} alt={organizationName} />
                ) : null}
                <AvatarFallback size="mini" roundness="rounded">
                  {organizationInitials ?? organizationName[0].toUpperCase()}
                </AvatarFallback>
              </Avatar>
              <span className="flex-1 truncate text-sm font-medium text-foreground">
                {organizationName}
              </span>
              {hasHeaderAction && (
                <button
                  type="button"
                  onClick={onHeaderAction}
                  title={headerActionTooltip}
                  className="flex size-xl cursor-pointer items-center justify-center rounded-md border-0 bg-transparent text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
                >
                  <ArrowRightLeft className="size-3.5" />
                </button>
              )}
            </div>
            <MenuSeparator />
          </>
        )}

        {/* Change workspace label + search */}
        <div className="px-3 pt-2.5 pb-1.5">
          <span className="text-xs font-medium text-muted-foreground">
            {t('appShell.sidebarWorkspacePopover.changeWorkspace')}
          </span>
        </div>
        <div className="px-3 pb-2">
          <InputGroup
            size="sm"
            className="w-full has-[input:focus-visible]:ring-0 has-[input:focus-visible]:ring-offset-0"
          >
            <InputDecoration muted>
              <Search className="size-3.5" />
            </InputDecoration>
            <Input
              placeholder={t('appShell.sidebarWorkspacePopover.searchWorkspaces')}
              value={searchQuery}
              onChange={(e) => onSearchChange(e.target.value)}
            />
          </InputGroup>
        </div>

        <MenuSeparator />

        {/* Workspace list */}
        <div className="min-h-0 flex-1 overflow-y-auto py-1">
          {workspaces.length === 0 ? (
            <div className="px-3 py-4 text-center text-sm text-muted-foreground">
              {t('appShell.sidebarWorkspacePopover.noWorkspacesFound')}
            </div>
          ) : (
            workspaces.map((ws) => {
              const isSelected = ws.id === selectedWorkspaceId;
              return (
                <button
                  key={ws.id}
                  type="button"
                  onClick={() => onSelect(ws)}
                  className="flex w-full cursor-pointer items-center gap-2 border-0 bg-transparent px-3 py-1.5 text-left transition-colors hover:bg-accent"
                >
                  <Avatar size="mini" roundness="rounded">
                    {ws.logoUrl ? <AvatarImage src={ws.logoUrl} alt={ws.name} /> : null}
                    <AvatarFallback size="mini" roundness="rounded">
                      {ws.name[0].toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                  <span className="flex-1 truncate text-sm text-foreground">{ws.name}</span>
                  {isSelected && <Check className="size-md shrink-0 text-primary" />}
                </button>
              );
            })
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
